import { BarChart, ChartPanel } from "@/components/analytics";
import { KpiReadout } from "@/components/ui/kpi-readout";

type DataQualityKind = "target_leakage" | "class_imbalance" | "label_noise";

interface DataQualityVizProps {
  kind: DataQualityKind;
  data: unknown;
}

interface TargetLeakageData {
  label: string;
  leaked: Array<{ kind: string; score: number; feature: string }>;
  borderline: Array<{ kind: string; score: number; feature: string }>;
}

interface ClassImbalanceData {
  label: string;
  classes: Array<{ value: string; count: number; ratio: number }>;
  severity: string;
  total_rows: number;
  recommendation: string;
  suggested_metric: string;
  warnings: string[];
}

interface LabelNoiseData {
  label: string;
  suspect_rate: number;
  suspect_count: number;
  suspects: Array<{
    index?: number;
    predicted?: string;
    actual?: string;
    confidence?: number;
  }>;
  scored_rows: number;
  n_folds: number;
  truncated: boolean;
  notes: string;
}

export function DataQualityViz({ kind, data }: DataQualityVizProps) {
  if (!data) return <p className="text-xs text-muted">No data available.</p>;

  if (kind === "target_leakage") {
    const typed = data as TargetLeakageData;
    const allFeatures = [...(typed.leaked || []), ...(typed.borderline || [])];

    if (allFeatures.length === 0) {
      return <p className="text-xs text-muted">No target leakage detected.</p>;
    }

    return (
      <div className="space-y-6">
        {allFeatures.length > 0 && (
          <ChartPanel title="Leakage Scores">
            <BarChart
              data={allFeatures}
              dataKey="score"
              nameKey="feature"
              layout="horizontal"
              height={Math.max(300, allFeatures.length * 40)}
            />
          </ChartPanel>
        )}

        {typed.leaked && typed.leaked.length > 0 && (
          <div>
            <h3 className="mb-3 font-mono text-sm font-semibold text-navy-600 dark:text-zinc-300">
              Leaked Features
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-navy-100 dark:border-zinc-800">
                  <tr>
                    <th className="px-4 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Feature
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Kind
                    </th>
                    <th className="px-4 py-2 text-right font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {typed.leaked.map((item) => (
                    <tr
                      key={item.feature}
                      className="border-b border-navy-100 dark:border-zinc-800"
                    >
                      <td className="px-4 py-2 font-mono text-navy-700 dark:text-zinc-100">
                        {item.feature}
                      </td>
                      <td className="px-4 py-2 font-mono text-navy-700 dark:text-zinc-100">
                        {item.kind}
                      </td>
                      <td className="px-4 py-2 font-mono tabular-nums text-navy-700 dark:text-zinc-100 text-right">
                        {item.score != null ? item.score.toFixed(3) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {typed.borderline && typed.borderline.length > 0 && (
          <div>
            <h3 className="mb-3 font-mono text-sm font-semibold text-navy-600 dark:text-zinc-300">
              Borderline Features
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-navy-100 dark:border-zinc-800">
                  <tr>
                    <th className="px-4 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Feature
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Kind
                    </th>
                    <th className="px-4 py-2 text-right font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Score
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {typed.borderline.map((item) => (
                    <tr
                      key={item.feature}
                      className="border-b border-navy-100 dark:border-zinc-800"
                    >
                      <td className="px-4 py-2 font-mono text-navy-700 dark:text-zinc-100">
                        {item.feature}
                      </td>
                      <td className="px-4 py-2 font-mono text-navy-700 dark:text-zinc-100">
                        {item.kind}
                      </td>
                      <td className="px-4 py-2 font-mono tabular-nums text-navy-700 dark:text-zinc-100 text-right">
                        {item.score != null ? item.score.toFixed(3) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  if (kind === "class_imbalance") {
    const typed = data as ClassImbalanceData;
    if (!typed.classes || !Array.isArray(typed.classes)) {
      return (
        <pre className="overflow-auto rounded-md bg-code-bg p-3 font-mono text-xs text-ink">
          {JSON.stringify(data, null, 2)}
        </pre>
      );
    }
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-px rounded-[14px] border border-line bg-line overflow-hidden">
          <KpiReadout label="Total Rows" value={typed.total_rows.toLocaleString()} />
          <KpiReadout label="Severity" value={typed.severity ?? "—"} accent />
          <KpiReadout label="Suggested Metric" value={typed.suggested_metric ?? "—"} />
          <KpiReadout label="Classes" value={typed.classes.length.toString()} />
        </div>

        {typed.recommendation && (
          <div className="rounded-md border border-line bg-surface p-3">
            <p className="font-mono text-xs text-ink">
              <strong>Recommendation:</strong> {typed.recommendation}
            </p>
          </div>
        )}

        {typed.warnings && typed.warnings.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-800 dark:bg-amber-900/20">
            {typed.warnings.map((warning) => (
              <p key={warning} className="font-mono text-xs text-amber-800 dark:text-amber-200">
                {warning}
              </p>
            ))}
          </div>
        )}

        <ChartPanel title="Class Distribution">
          <BarChart
            data={typed.classes}
            dataKey="count"
            nameKey="value"
            height={Math.max(300, typed.classes.length * 40)}
          />
        </ChartPanel>
      </div>
    );
  }

  if (kind === "label_noise") {
    const typed = data as LabelNoiseData;
    if (!typed.suspects || !Array.isArray(typed.suspects)) {
      return (
        <pre className="overflow-auto rounded-md bg-code-bg p-3 font-mono text-xs text-ink">
          {JSON.stringify(data, null, 2)}
        </pre>
      );
    }

    const hasSuspects = typed.suspect_count > 0;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-4 gap-px rounded-[14px] border border-line bg-line overflow-hidden">
          <KpiReadout
            label="Suspect Rate"
            value={((typed.suspect_rate ?? 0) * 100).toFixed(1)}
            suffix="%"
            accent={typed.suspect_rate > 0.05}
          />
          <KpiReadout
            label="Suspect Count"
            value={typed.suspect_count.toString()}
            accent={typed.suspect_count > 0}
          />
          <KpiReadout label="Scored Rows" value={typed.scored_rows.toLocaleString()} />
          <KpiReadout label="Folds" value={typed.n_folds.toString()} />
        </div>

        {typed.notes && (
          <div className="rounded-md border border-line bg-surface p-3">
            <p className="font-mono text-xs text-ink">{typed.notes}</p>
          </div>
        )}

        {!hasSuspects && <p className="text-xs text-muted">No suspicious labels detected.</p>}

        {hasSuspects && (
          <ChartPanel title="Suspected Noisy Labels">
            <div className="max-h-[300px] overflow-y-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-navy-100 dark:border-zinc-800">
                  <tr>
                    <th className="px-4 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Index
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Predicted
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Actual
                    </th>
                    <th className="px-4 py-2 text-left font-mono text-[10px] font-semibold uppercase tracking-[0.13em] text-navy-300">
                      Confidence
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {typed.suspects.slice(0, 50).map((item, idx) => (
                    <tr
                      key={item.index ?? idx}
                      className="border-b border-navy-100 dark:border-zinc-800"
                    >
                      <td className="px-4 py-2 font-mono tabular-nums text-navy-700 dark:text-zinc-100">
                        {item.index ?? "—"}
                      </td>
                      <td className="px-4 py-2 font-mono text-navy-700 dark:text-zinc-100">
                        {item.predicted ?? "—"}
                      </td>
                      <td className="px-4 py-2 font-mono text-navy-700 dark:text-zinc-100">
                        {item.actual ?? "—"}
                      </td>
                      <td className="px-4 py-2 font-mono tabular-nums text-navy-700 dark:text-zinc-100">
                        {item.confidence != null ? item.confidence.toFixed(2) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {typed.truncated && (
                <p className="mt-2 text-xs text-muted italic">List truncated to 50 entries...</p>
              )}
            </div>
          </ChartPanel>
        )}
      </div>
    );
  }

  return null;
}
